import { supabase } from '@/utils/supabase';

export const TALENT_INTAKE_EMAIL = 'join@lendwisemtg.com';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export interface TalentRecipient {
  id: string;
  email: string;
  fullName: string;
}

export function normalizeEmail(email: unknown): string | null {
  if (typeof email !== 'string') {
    return null;
  }
  const normalized = email.trim().toLowerCase();
  if (!normalized || !EMAIL_RE.test(normalized)) {
    return null;
  }
  return normalized;
}

function splitEmails(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.flatMap(splitEmails);
  }
  if (typeof value !== 'string') {
    return [];
  }
  return value
    .split(/[;,]/)
    .map((email) => email.trim())
    .filter(Boolean);
}

export function mergeEmails(values: unknown[], blockedEmails: unknown[] = []): string[] {
  const blocked = new Set(
    blockedEmails
      .flatMap(splitEmails)
      .map(normalizeEmail)
      .filter((email): email is string => Boolean(email))
  );
  const seen = new Set<string>();
  const out: string[] = [];

  for (const value of values) {
    for (const raw of splitEmails(value)) {
      const email = normalizeEmail(raw);
      if (!email || blocked.has(email) || seen.has(email)) {
        continue;
      }
      seen.add(email);
      out.push(email);
    }
  }

  return out;
}

export function uniqueUserIds(userIds: Array<string | null | undefined>): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const userId of userIds) {
    const normalized = typeof userId === 'string' ? userId.trim().toLowerCase() : '';
    if (!normalized || !UUID_RE.test(normalized) || seen.has(normalized)) {
      continue;
    }
    seen.add(normalized);
    out.push(normalized);
  }
  return out;
}

export async function resolveActiveTalentUsers(userIds: Array<string | null | undefined>): Promise<TalentRecipient[]> {
  const ids = uniqueUserIds(userIds);
  if (ids.length === 0) {
    return [];
  }

  const { data, error } = await supabase
    .rpc('resolve_talent_owner_users', { p_user_ids: ids });

  if (error) {
    return [];
  }

  const usersById = new Map<string, {
    id: string;
    email: string | null;
    first_name: string | null;
    last_name: string | null;
    full_name: string | null;
  }>();

  for (const user of data || []) {
    if (typeof user.id === 'string') {
      usersById.set(user.id.toLowerCase(), user);
    }
  }

  const recipients: TalentRecipient[] = [];
  for (const id of ids) {
    const user = usersById.get(id);
    const email = normalizeEmail(user?.email);
    if (!user || !email) {
      continue;
    }
    recipients.push({
      id: user.id,
      email,
      fullName: user.full_name || `${user.first_name || ''} ${user.last_name || ''}`.trim() || email
    });
  }
  return recipients;
}

export async function resolveTalentCcEmails({
  toEmails,
  ownerUserIds,
  extraEmails = []
}: {
  toEmails: unknown[];
  ownerUserIds: Array<string | null | undefined>;
  extraEmails?: unknown[];
}): Promise<string[]> {
  const owners = await resolveActiveTalentUsers(ownerUserIds);
  return mergeEmails(
    [
      owners.map((owner) => owner.email),
      TALENT_INTAKE_EMAIL,
      ...extraEmails
    ],
    toEmails
  );
}
